import { API_URL } from './apiBase';
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// AI Chat
export async function sendAIQuery(params, options = {}) {
  try {
    const response = await fetch(`${API_URL}/api/ai-chat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      signal: options.signal,
    });
    if (!response.ok) {
      // Backend returns HTTP 502/503 with {detail:"..."} for LLM failures
      // (budget exhausted, parse errors). Surface the friendly detail to
      // the user instead of a raw status code. For Cloudflare-class errors
      // (5xx without a JSON body — typically 520/521/522 timeouts) we
      // emit a humane "service is busy" message instead of "AI service
      // error: 520" which scares non-technical users.
      let friendly = response.status >= 520 && response.status < 530
        ? 'Our travel planner is taking a moment — please try again in a few seconds.'
        : `Our travel planner is temporarily unavailable (${response.status}). Please try again shortly.`;
      try {
        const body = await response.json();
        if (body?.detail) friendly = typeof body.detail === 'string' ? body.detail : friendly;
      } catch { /* response was not JSON — keep the friendly default */ }
      return { success: false, error: friendly, status: response.status };
    }
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Streaming AI Chat — same single LLM call as sendAIQuery, but the
 * server emits SSE events as the pipeline runs so the UI can paint
 * the trip-plan card the moment the LLM responds (instead of waiting
 * for the full hotel/flight/alternatives post-processing).
 *
 * Events emitted by the server (see /api/ai-chat/stream docstring):
 *   thinking → meta → thinking → trip_plan → hotels → flights →
 *   alternatives → complete   (or `error` on failure).
 *
 * @param {Object} params - same payload shape as sendAIQuery.
 * @param {Object} handlers - optional. Any subset of:
 *      onThinking({phase, sessionId?})
 *      onMeta({style, discovery, hasBudgetBlock, ...})
 *      onTripPlan({tripPlan, message, hotelSearchContext, servedBy, sessionId})
 *      onHotels({hotels})
 *      onFlights({flights})
 *      onAlternatives({alternatives})
 *      onComplete(envelope)   ← envelope.data is the full payload
 *      onError({status, detail})
 * @returns {Promise<{success, data?, error?, status?}>}  same shape as
 *      sendAIQuery — resolves with the COMPLETE envelope (or error).
 *      Use the callbacks for incremental painting; use the return
 *      value for the "final state" replace.
 */
export async function streamAIQuery(params, handlers = {}, options = {}) {
  let errorEmitted = false;
  let completedEnvelope = null;
  const reportError = (detail, status) => {
    if (errorEmitted) return;
    errorEmitted = true;
    handlers.onError?.({ detail, status });
  };

  try {
    const response = await fetch(`${API_URL}/api/ai-chat/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      body: JSON.stringify(params),
      signal: options.signal,
    });

    // Only retry through the older route when streaming is NOT IMPLEMENTED.
    // A 401, 429, 5xx or interrupted response may already represent a paid
    // upstream LLM request. Blind fallback caused duplicate AI generations.
    if ([404, 405, 501].includes(response.status) || (response.ok && !response.body)) {
      const fallback = await sendAIQuery(params, options);
      if (options.signal?.aborted) return { success: false, aborted: true, error: 'cancelled' };
      if (fallback.success && fallback.data) {
        completedEnvelope = fallback;
        handlers.onComplete?.(fallback);
      } else {
        reportError(fallback.error || 'AI service unavailable, please try again.', fallback.status);
      }
      return fallback;
    }

    if (options.signal?.aborted) return { success: false, aborted: true, error: 'cancelled' };
    if (!response.ok) {
      let detail = response.status === 429
        ? 'The travel planner is busy. Please try again in a moment.'
        : 'The travel planner is temporarily unavailable. Please try again.';
      try {
        const json = await response.json();
        if (typeof json?.detail === 'string' && response.status < 500) detail = json.detail;
      } catch { /* upstream or edge may return HTML */ }
      reportError(detail, response.status);
      return { success: false, error: detail, status: response.status };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let serverError = null;

    const dispatch = (name, data) => {
      if (completedEnvelope || serverError) return;
      switch (name) {
        case 'thinking': handlers.onThinking?.(data); break;
        case 'meta': handlers.onMeta?.(data); break;
        case 'trip_plan': handlers.onTripPlan?.(data); break;
        case 'hotels': handlers.onHotels?.(data); break;
        case 'flights': handlers.onFlights?.(data); break;
        case 'alternatives': handlers.onAlternatives?.(data); break;
        case 'complete':
          completedEnvelope = data;
          handlers.onComplete?.(data);
          break;
        case 'error':
          serverError = data || {};
          break;
        default: break;
      }
    };

    // Handle \r\n framing from proxies and JSON frames split across chunks.
    const readFrames = () => {
      buffer = buffer.replace(/\r\n/g, '\n');
      let separator;
      while ((separator = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, separator);
        buffer = buffer.slice(separator + 2);
        let name = 'message';
        const dataLines = [];
        for (const line of rawEvent.split('\n')) {
          if (line.startsWith('event:')) name = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        }
        if (!dataLines.length) continue;
        try { dispatch(name, JSON.parse(dataLines.join('\n'))); }
        catch { /* malformed supplier or proxy frame; never expose internals */ }
      }
    };

    while (!completedEnvelope && !serverError) {
      if (options.signal?.aborted) return { success: false, aborted: true, error: 'cancelled' };
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      readFrames();
      // Cap incomplete frame buffers; oversized/unframed data should fail
      // safely instead of consuming unlimited browser memory.
      if (buffer.length > 1024 * 1024) {
        serverError = { detail: 'The travel planner response was too large.' };
      }
    }
    if (completedEnvelope) {
      try { await reader.cancel(); } catch { /* finished response */ }
      return completedEnvelope;
    }
    if (serverError) {
      const detail = serverError.detail || serverError.message || 'AI service unavailable.';
      reportError(detail, serverError.status);
      return { success: false, error: detail, status: serverError.status };
    }

    // The server already began producing an answer. A second full request
    // would charge twice and may yield a completely different itinerary.
    const detail = 'The travel planner connection was interrupted. Please try again.';
    reportError(detail);
    return { success: false, error: detail };
  } catch (error) {
    if (options.signal?.aborted || error?.name === 'AbortError') {
      return { success: false, aborted: true, error: 'cancelled' };
    }
    if (completedEnvelope) return completedEnvelope;
    const detail = 'The travel planner connection failed. Please try again.';
    reportError(detail);
    return { success: false, error: detail };
  }
}

// Chat History
export async function getChatHistory(userId) {
  try {
    const response = await fetch(`${API_URL}/api/chat-history/${userId}`, { credentials: 'include' });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Best Flight Deals (cheapest flights from a given origin via Travelpayouts)
export async function getFlightDeals(origin = 'SOF', limit = 6, { signal, rotation = 0 } = {}) {
  try {
    const response = await fetch(
      `${API_URL}/api/flights/deals?origin=${encodeURIComponent(origin)}&limit=${limit}&rotation=${Math.max(0, Math.min(100, Number(rotation) || 0))}`,
      { signal },
    );
    if (!response.ok) throw new Error(`Deals service error: ${response.status}`);
    return await response.json();
  } catch (error) {
    if (signal?.aborted || error?.name === 'AbortError') {
      return { success: false, aborted: true, deals: [] };
    }
    return { success: false, error: 'Deals are temporarily unavailable.', deals: [] };
  }
}

// Save Trip
export async function saveTrip(tripData) {
  try {
    const response = await fetch(`${API_URL}/api/trips/save`, {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tripData),
    });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Get Saved Trips
export async function getSavedTrips(userId) {
  try {
    const response = await fetch(`${API_URL}/api/trips/${userId}`, { credentials: 'include' });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Delete Trip
export async function deleteTrip(tripId) {
  try {
    const response = await fetch(`${API_URL}/api/trips/${tripId}`, {
      credentials: 'include',
      method: 'DELETE',
    });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Create Booking
export async function createBooking(bookingData) {
  try {
    const response = await fetch(`${API_URL}/api/bookings/create`, {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Get User Bookings
export async function getUserBookings(userId) {
  try {
    const response = await fetch(`${API_URL}/api/bookings/user/${userId}`, { credentials: 'include' });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Create Checkout Session
export async function createCheckoutSession(bookingId) {
  try {
    const response = await fetch(`${API_URL}/api/payments/checkout`, {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bookingId,
        originUrl: window.location.origin,
      }),
    });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Get Payment Status
export async function getPaymentStatus(sessionId) {
  try {
    const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, { credentials: 'include' });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Get Dashboard
export async function getDashboard(userId) {
  try {
    const response = await fetch(`${API_URL}/api/dashboard/${userId}`, { credentials: 'include' });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Get User Profile
export async function getUserProfile(userId) {
  try {
    const response = await fetch(`${API_URL}/api/user/profile/${userId}`, { credentials: 'include' });
    const result = await response.json();
    if (!response.ok) return { success: false, error: result.detail || result.error || 'Request denied' };
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Send Email
export async function sendEmail(emailData) {
  try {
    const response = await fetch(`${API_URL}/api/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emailData),
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Hotel Search (Supabase Edge Function - for when you have API keys)
export async function searchHotels(params) {
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/hotelbeds-search`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    if (!response.ok) throw new Error('Failed to search hotels');
    const result = await response.json();
    return result.hotels || [];
  } catch (error) {
    throw error;
  }
}

// Flight Search (Supabase Edge Function - for when you have API keys)
export async function searchFlights(params) {
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/flight-search`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    if (!response.ok) throw new Error('Failed to search flights');
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}
